/**
 * Format utilities for displaying data in human-readable formats
 */
import { format, parseISO } from 'date-fns';
import { sv } from 'date-fns/locale';

/**
 * Format file size in bytes to human-readable format (B, KB, MB, GB)
 */
export const formatFileSize = (bytes: number): string => {
  const sizes = ['B', 'KB', 'MB', 'GB'];
  if (bytes === 0) return '0 B';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Format date to Swedish locale string (YYYY-MM-DD).
 *
 * @param date ISO string or Date.
 * @returns The formatted date, or '' for a missing/unparseable value — a
 *          caller that renders the result must never show "Invalid Date".
 */
export const formatDate = (date: Date | string | null | undefined): string => {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('sv-SE');
};

/**
 * Format date and time to Swedish locale string
 */
export const formatDateTime = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleString('sv-SE');
};

/**
 * "3 sep. 2026 07:45" — a timestamp where the time of day matters.
 *
 * Pinned to Europe/Stockholm on purpose: the value describes an event on a
 * Swedish farm and must read the same for an inspector whose laptop is set to
 * another timezone. It also makes the output testable without a TZ env var.
 *
 * @param value ISO string or Date.
 * @returns The formatted timestamp, or '' for an unparseable value.
 */
export const formatSwedishDateTime = (value: Date | string): string => {
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('sv-SE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Stockholm',
  }).format(d);
};

/**
 * "3 sep. 2026" — a calendar day the way the pärm and the control page print
 * it (a validity date, an upload date).
 *
 * @param value ISO string (date-only or timestamp) or Date.
 * @returns The formatted day, or '' for a missing or unparseable value — a
 *          caller that renders the result must never show "Invalid Date".
 */
export const formatSwedishDate = (value: Date | string | null | undefined): string => {
  if (!value) return '';
  const d = typeof value === 'string' ? parseISO(value) : value;
  if (Number.isNaN(d.getTime())) return '';
  return format(d, 'd MMM yyyy', { locale: sv });
};

/**
 * Serialize a Date to a date-only string (YYYY-MM-DD) in LOCAL time.
 *
 * Never use `toISOString().split('T')[0]` for this: a DatePicker value is
 * local midnight, which toISOString() shifts to the previous day for every
 * timezone east of UTC (all Swedish users).
 */
export const toDateOnlyString = (date: Date): string => {
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parse a date-only string (YYYY-MM-DD) into a Date at LOCAL midnight.
 *
 * The inverse of `toDateOnlyString`. `new Date('2026-12-31')` would give UTC
 * midnight, which a picker in a negative-offset timezone shows as the day
 * before; constructing from the parts keeps the calendar day intact.
 *
 * @param value A YYYY-MM-DD string. A longer ISO string is truncated to its date part.
 * @returns The Date, or null when the string does not start with a real calendar
 *   day (`2026-02-31` is null, not 3 March).
 */
export const parseDateOnlyString = (value: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  // setFullYear rather than the constructor: `new Date(2, ...)` means 1902.
  const date = new Date(0);
  date.setFullYear(year, month - 1, day);
  date.setHours(0, 0, 0, 0);
  const isRealDay =
    date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  return isRealDay ? date : null;
};
